import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import type { CSSProperties } from 'react'
import {
  ArrowRight,
  ArrowUpRight,
  PlayCircle,
  Search,
  Bell,
  ShieldCheck,
  Users,
  CalendarCheck,
  Receipt,
  FlaskConical,
  Quote,
  Plus,
  Twitter,
  Linkedin,
  Instagram,
  Heart,
  Check,
} from 'lucide-react'

// The public page is built entirely on the MediLux design base:
//   · Color via the runtime --ml-* tokens (recolors with the active theme)
//   · Newsreader (font-headline) for structural headings, Manrope (font-body) for UI
//   · The single 8px corner system; rounded-full only for avatars/dots
//   · The login "scene" brand-mesh language for the hero + outcomes bands
//
// Motion follows Emil Kowalski's animation standards (animations.dev):
//   · Strong custom ease-out; transform/opacity only (GPU); UI feedback <300ms
//   · Scroll reveals enhance an already-visible default — the hidden state is
//     applied by JS, so with JS off / reduced-motion the content just shows
//   · Press feedback (scale 0.97) and hover nudges gated for touch; full a11y

// Themed brand-mesh used by the immersive dark bands (hero panel + outcomes).
// Mirrors AuthLayout.css: a deep floor that carries white text at AA, lit by the
// vibrant (decorative-only) gradient/accent tones. Recolors per specialty theme.
const sceneMesh: CSSProperties = {
  backgroundColor: 'rgb(var(--ml-brand-deep))',
  backgroundImage: [
    'radial-gradient(115% 120% at 84% 6%, rgb(var(--ml-brand-hi) / 0.55) 0%, transparent 56%)',
    'radial-gradient(85% 95% at 10% 92%, rgb(var(--ml-brand-accent) / 0.38) 0%, transparent 60%)',
    'radial-gradient(95% 95% at 24% 22%, rgb(var(--ml-brand-mid) / 0.55) 0%, transparent 62%)',
    'linear-gradient(150deg, rgb(var(--ml-brand-deep-2)) 0%, rgb(var(--ml-brand-deep)) 62%)',
  ].join(', '),
}

// Per-item reveal delay (stagger). Kept in the 30–80ms band per the standards.
const delay = (ms: number): CSSProperties => ({ '--reveal-delay': `${ms}ms` } as CSSProperties)

const features = [
  {
    icon: Users,
    kicker: 'Expediente',
    title: 'La historia clínica, legible de un vistazo',
    desc: 'Notas, recetas y diagnósticos organizados como un expediente que se lee, no que se descifra. Todo el contexto del paciente en un solo lugar, listo cuando la consulta lo exige.',
    points: ['Línea de tiempo unificada', 'Búsqueda instantánea', 'Cumplimiento NOM-004'],
  },
  {
    icon: CalendarCheck,
    kicker: 'Agenda',
    title: 'Una agenda que reduce el ausentismo',
    desc: 'Reserva fluida con recordatorios automáticos por WhatsApp. El equipo ve el día completo de un golpe de vista y el tiempo de la consulta se aprovecha por entero.',
    points: ['Recordatorios automáticos', 'Vista de día y semana', 'Sincronización con calendario'],
  },
  {
    icon: Receipt,
    kicker: 'Facturación',
    title: 'Cobros y seguros sin fricción',
    desc: 'Pagos, presupuestos y facturación CFDI en una interfaz que mantiene lo financiero ordenado y discreto, sin sacar al equipo del flujo de atención.',
    points: ['Facturación CFDI 4.0', 'Presupuestos y anticipos', 'Conciliación clara'],
  },
]

const outcomes = [
  { value: '40%', label: 'menos tiempo administrativo por consulta' },
  { value: '99%', label: 'de historiales encontrados al primer intento' },
  { value: '2 pasos', label: 'para llegar a cualquier flujo frecuente' },
  { value: '24/7', label: 'respaldo cifrado de cada expediente' },
]

// Scroll-reveal: JS applies the hidden state, then reveals on intersect (one-shot).
// With JS off or reduced motion this never runs, so [data-reveal] content stays
// fully visible — the reveal enhances an already-visible default, never gates it.
function useScrollReveal(rootRef: React.RefObject<HTMLElement>) {
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduce || !('IntersectionObserver' in window)) return

    const els = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]'))
    root.classList.add('reveal-ready') // arms the hidden initial state (CSS-scoped)

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible')
            io.unobserve(e.target)
          }
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.12 },
    )

    // Elements already in view on load are shown in the same frame (no flash,
    // no entrance); only genuinely below-fold elements get observed to animate.
    for (const el of els) {
      if (el.getBoundingClientRect().top < window.innerHeight * 0.92) {
        el.classList.add('is-visible')
      } else {
        io.observe(el)
      }
    }
    return () => io.disconnect()
  }, [rootRef])
}

export function LandingPage() {
  const navigate = useNavigate()
  const goLogin = () => navigate('/login')
  const rootRef = useRef<HTMLDivElement>(null)
  useScrollReveal(rootRef)

  return (
    <div
      ref={rootRef}
      className="ml-landing min-h-screen overflow-x-hidden bg-ml-surface font-body text-ml-on-surface antialiased"
    >
      <style>{`
        .ml-landing {
          --ease-out: cubic-bezier(0.23, 1, 0.32, 1);
        }
        /* Load-in for the hero (runs once; content settles to a visible default) */
        @keyframes ml-rise { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: none; } }
        @keyframes ml-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        .ml-rise { animation: ml-rise 0.72s var(--ease-out) both; }
        .ml-float { animation: ml-float 6.5s ease-in-out infinite; }

        /* Scroll reveals — transform/opacity only, strong ease-out, staggered.
           Hidden state applies ONLY under .reveal-ready (added by JS). */
        .ml-landing [data-reveal] {
          transition: opacity 0.7s var(--ease-out), transform 0.7s var(--ease-out);
          transition-delay: var(--reveal-delay, 0ms);
          will-change: opacity, transform;
        }
        .ml-landing.reveal-ready [data-reveal] { opacity: 0; transform: translateY(22px); }
        .ml-landing.reveal-ready [data-reveal].is-visible { opacity: 1; transform: none; }

        /* Press feedback + hover nudge (Emil signatures). */
        .ml-cta {
          transition: transform 0.16s var(--ease-out), background-color 0.2s var(--ease-out),
            box-shadow 0.2s var(--ease-out), color 0.2s var(--ease-out);
        }
        .ml-cta:active { transform: scale(0.97); }
        .ml-arrow { transition: transform 0.2s var(--ease-out); }
        @media (hover: hover) and (pointer: fine) {
          .ml-cta:hover .ml-arrow { transform: translateX(3px); }
          .ml-cta:hover .ml-arrow-diag { transform: translate(2px, -2px); }
        }

        @media (prefers-reduced-motion: reduce) {
          .ml-rise, .ml-float { animation: none !important; }
          .ml-landing [data-reveal] { transition: none !important; opacity: 1 !important; transform: none !important; }
          .ml-cta:active { transform: none; }
        }
      `}</style>

      {/* ─────────────────────────── NAV ─────────────────────────── */}
      <header className="sticky top-0 z-40 border-b border-ml-outline-variant/60 bg-ml-surface/85 backdrop-blur-md">
        <nav className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-10">
            <a href="/" className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-ml-primary text-ml-on-primary">
                <Heart className="h-5 w-5" />
              </span>
              <span className="flex flex-col leading-none">
                <span className="font-headline text-xl italic text-ml-primary">MediLux</span>
                <span className="mt-0.5 text-[0.6rem] uppercase tracking-[0.2em] text-ml-on-surface-variant">
                  Clinical Atelier
                </span>
              </span>
            </a>
            <div className="hidden items-center gap-8 md:flex">
              {[
                ['Plataforma', '#plataforma'],
                ['Resultados', '#resultados'],
                ['Historias', '#historias'],
              ].map(([label, href]) => (
                <a
                  key={href}
                  href={href}
                  className="text-sm font-medium text-ml-on-surface-variant transition-colors duration-200 hover:text-ml-primary"
                >
                  {label}
                </a>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-5">
            <button
              onClick={goLogin}
              className="ml-cta hidden text-sm font-semibold text-ml-primary hover:text-ml-primary-container sm:block"
            >
              Iniciar sesión
            </button>
            <button
              onClick={goLogin}
              className="ml-cta group inline-flex items-center gap-2 rounded-lg bg-ml-primary px-5 py-2.5 text-sm font-semibold text-ml-on-primary shadow-sm hover:bg-ml-primary-container hover:shadow-md"
            >
              Solicitar acceso
              <ArrowRight className="ml-arrow h-4 w-4" />
            </button>
          </div>
        </nav>
      </header>

      {/* ─────────────────────────── HERO ─────────────────────────── */}
      <section className="relative mx-auto grid max-w-6xl grid-cols-1 items-center gap-14 px-6 py-16 lg:grid-cols-[1.05fr_1fr] lg:py-24">
        {/* Left — copy */}
        <div className="flex flex-col items-start">
          <span className="ml-rise inline-flex items-center gap-2 rounded-lg border border-ml-primary/15 bg-ml-primary/[0.06] px-3 py-1.5 text-xs font-semibold text-ml-primary">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ml-primary opacity-60 motion-reduce:animate-none" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-ml-primary" />
            </span>
            Diseñado para clínicas privadas en México
          </span>

          <h1
            className="ml-rise mt-7 font-headline text-[2.75rem] font-light leading-[1.1] tracking-[-0.02em] text-ml-on-surface sm:text-6xl"
            style={{ animationDelay: '60ms', textWrap: 'balance' as CSSProperties['textWrap'] }}
          >
            El sistema clínico que trabaja tan{' '}
            <span className="italic text-ml-primary">duro como usted</span>.
          </h1>

          <p
            className="ml-rise mt-6 max-w-xl text-lg leading-relaxed text-ml-on-surface-variant"
            style={{ animationDelay: '120ms' }}
          >
            Expediente, agenda, farmacia, laboratorio y facturación en una sola plataforma
            tranquila y precisa. Menos software entre usted y sus pacientes.
          </p>

          <div
            className="ml-rise mt-9 flex w-full flex-col gap-3 sm:w-auto sm:flex-row"
            style={{ animationDelay: '180ms' }}
          >
            <button
              onClick={goLogin}
              className="ml-cta group inline-flex items-center justify-center gap-2 rounded-lg bg-ml-primary px-7 py-3.5 font-semibold text-ml-on-primary shadow-lg shadow-ml-primary/20 hover:bg-ml-primary-container hover:shadow-xl hover:shadow-ml-primary/25"
            >
              Comenzar ahora
              <ArrowRight className="ml-arrow h-4 w-4" />
            </button>
            <button className="ml-cta inline-flex items-center justify-center gap-2 rounded-lg border border-ml-outline-variant bg-ml-surface-container-lowest px-7 py-3.5 font-semibold text-ml-primary hover:bg-ml-surface-container-high">
              <PlayCircle className="h-5 w-5" />
              Ver demostración
            </button>
          </div>

          <div
            className="ml-rise mt-11 flex items-center gap-4 border-t border-ml-outline-variant/60 pt-6"
            style={{ animationDelay: '240ms' }}
          >
            <div className="flex -space-x-3">
              {[
                { i: 'ER', bg: 'bg-ml-primary text-ml-on-primary' },
                { i: 'JM', bg: 'bg-ml-secondary text-white' },
                { i: 'AG', bg: 'bg-ml-tertiary text-white' },
              ].map((a) => (
                <span
                  key={a.i}
                  className={`flex h-10 w-10 items-center justify-center rounded-full border-2 border-ml-surface text-xs font-semibold ${a.bg}`}
                >
                  {a.i}
                </span>
              ))}
            </div>
            <p className="text-sm text-ml-on-surface-variant">
              La confianza de más de{' '}
              <span className="font-headline text-lg italic text-ml-primary">200</span> clínicas
              en operación.
            </p>
          </div>
        </div>

        {/* Right — immersive themed scene panel + floating product card */}
        <div className="ml-rise relative" style={{ animationDelay: '160ms' }}>
          <div
            className="relative overflow-hidden rounded-lg p-6 shadow-2xl shadow-ml-primary/20 sm:p-8"
            style={sceneMesh}
          >
            {/* grain / sheen */}
            <div className="pointer-events-none absolute inset-0 rounded-lg ring-1 ring-inset ring-white/10" />

            <div className="relative flex items-center justify-between text-white/90">
              <div>
                <p className="font-headline text-2xl italic">Gestión de pacientes</p>
                <p className="mt-1 text-xs text-white/60">Atelier clínico · turno matutino</p>
              </div>
              <div className="flex items-center gap-3">
                <Search className="h-4 w-4 text-white/60" />
                <div className="relative">
                  <Bell className="h-4 w-4 text-white/60" />
                  <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-[rgb(var(--ml-brand-glow))]" />
                </div>
              </div>
            </div>

            {/* stat strip */}
            <div className="relative mt-6 grid grid-cols-3 gap-3">
              {[
                { v: '2,458', l: 'Pacientes' },
                { v: '14', l: 'Citas hoy' },
                { v: '+24', l: 'Nuevos' },
              ].map((s) => (
                <div key={s.l} className="rounded-lg bg-white/10 px-3 py-3 backdrop-blur-sm">
                  <p className="font-headline text-2xl leading-none text-white">{s.v}</p>
                  <p className="mt-1.5 text-[0.65rem] uppercase tracking-wide text-white/55">{s.l}</p>
                </div>
              ))}
            </div>

            {/* patient rows — the "product screenshot" */}
            <div className="relative mt-4 space-y-2.5">
              {[
                { i: 'AR', n: 'Ana Rothschild', m: 'EXP-1823 · 32 años', s: 'Activo' },
                { i: 'HM', n: 'Hugo Medina', m: 'EXP-9093 · 47 años', s: 'En sala' },
              ].map((p) => (
                <div
                  key={p.i}
                  className="flex items-center gap-3.5 rounded-lg bg-ml-surface-container-lowest/95 px-4 py-3 shadow-sm"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-ml-primary/10 text-sm font-semibold text-ml-primary">
                    {p.i}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-headline text-base text-ml-on-surface">{p.n}</p>
                    <p className="truncate text-xs text-ml-on-surface-variant">{p.m}</p>
                  </div>
                  <span className="rounded-lg bg-success/10 px-2.5 py-1 text-[0.7rem] font-semibold text-success">
                    {p.s}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* floating trust chip */}
          <div className="ml-float absolute -bottom-5 -left-4 hidden items-center gap-3 rounded-lg border border-ml-outline-variant bg-ml-surface-container-lowest px-4 py-3 shadow-xl sm:flex">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-ml-primary/10 text-ml-primary">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <div>
              <p className="text-sm font-semibold text-ml-on-surface">Cifrado y respaldado</p>
              <p className="text-xs text-ml-on-surface-variant">Cada expediente, siempre</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────── TRUSTED-BY STRIP ─────────────────── */}
      <section className="border-y border-ml-outline-variant/50 bg-ml-surface-container-low">
        <div
          data-reveal
          className="mx-auto flex max-w-6xl flex-col items-center gap-8 px-6 py-10"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-ml-on-surface-variant">
            En operación en clínicas de especialidad
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6 font-headline text-lg text-ml-on-surface-variant/80">
            <span className="flex items-center gap-2">
              <Plus className="h-5 w-5 rotate-45 text-ml-primary" /> Sanitas Prime
            </span>
            <span className="italic">Lumina Health</span>
            <span className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-ml-primary" /> Aura Medical
            </span>
            <span className="text-ml-primary">
              Boutique <span className="italic">Care</span>
            </span>
            <span className="flex items-center gap-2">
              <FlaskConical className="h-5 w-5 text-ml-primary" /> GeneClinic
            </span>
          </div>
        </div>
      </section>

      {/* ─────────────────────── FEATURES ─────────────────────── */}
      <section id="plataforma" className="mx-auto max-w-6xl px-6 py-20 lg:py-28">
        <div data-reveal className="max-w-2xl">
          <h2 className="font-headline text-3xl font-light leading-tight tracking-[-0.02em] text-ml-primary sm:text-4xl">
            Todo el flujo clínico, una sola herramienta tranquila
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-ml-on-surface-variant">
            Reemplazamos el papel y las herramientas dispersas por una interfaz predecible,
            para que el equipo se concentre en el paciente, no en el sistema.
          </p>
        </div>

        {/* Editorial alternating rows — not a uniform card grid */}
        <div className="mt-14 divide-y divide-ml-outline-variant/60 border-y border-ml-outline-variant/60">
          {features.map((f, idx) => (
            <div
              key={f.title}
              data-reveal
              style={delay((idx % 3) * 80)}
              className={`grid grid-cols-1 items-center gap-8 py-10 lg:grid-cols-12 ${
                idx % 2 === 1 ? 'lg:[&>*:first-child]:order-2' : ''
              }`}
            >
              <div className="lg:col-span-7">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-ml-primary/[0.08] text-ml-primary">
                    <f.icon className="h-5 w-5" />
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-ml-secondary">
                    {f.kicker}
                  </span>
                </div>
                <h3 className="mt-5 font-headline text-2xl leading-snug text-ml-on-surface">
                  {f.title}
                </h3>
                <p className="mt-3 max-w-xl leading-relaxed text-ml-on-surface-variant">{f.desc}</p>
              </div>
              <div className="lg:col-span-5">
                <ul className="grid gap-2.5">
                  {f.points.map((p) => (
                    <li
                      key={p}
                      className="flex items-center gap-3 rounded-lg bg-ml-surface-container-low px-4 py-3 text-sm font-medium text-ml-on-surface"
                    >
                      <Check className="h-4 w-4 shrink-0 text-ml-primary" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─────────────────── OUTCOMES (drenched band) ─────────────────── */}
      <section id="resultados" className="relative overflow-hidden" style={sceneMesh}>
        <div className="relative mx-auto grid max-w-6xl grid-cols-1 gap-14 px-6 py-20 lg:grid-cols-[1fr_1.1fr] lg:items-center lg:py-28">
          <div data-reveal>
            <h2 className="font-headline text-3xl font-light leading-tight tracking-[-0.02em] text-white sm:text-5xl">
              Resultados que se sienten en cada consulta
            </h2>
            <p className="mt-5 max-w-md text-lg leading-relaxed text-white/70">
              MediLux no solo se ve mejor. Cambia cómo opera la clínica: menos fricción
              administrativa, más tiempo con el paciente.
            </p>
            <button
              onClick={goLogin}
              className="ml-cta group mt-9 inline-flex items-center gap-2 rounded-lg bg-ml-surface-container-lowest px-7 py-3.5 font-semibold text-ml-primary hover:bg-white"
            >
              Ver la plataforma
              <ArrowUpRight className="ml-arrow-diag h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg bg-white/10">
            {outcomes.map((o, i) => (
              <div
                key={o.label}
                data-reveal
                style={delay(i * 60)}
                className="bg-transparent px-6 py-8 backdrop-blur-[1px]"
              >
                <p className="font-headline text-4xl leading-none text-white sm:text-5xl">{o.value}</p>
                <p className="mt-3 text-sm leading-snug text-white/65">{o.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────── TESTIMONIAL ─────────────────────── */}
      <section id="historias" className="mx-auto max-w-4xl px-6 py-24 text-center lg:py-32">
        <div data-reveal>
          <Quote className="mx-auto h-10 w-10 text-ml-primary/25" />
          <blockquote className="mt-8 font-headline text-2xl font-light leading-snug tracking-[-0.01em] text-ml-primary sm:text-4xl">
            «MediLux elevó la percepción de nuestra clínica. Los pacientes notan el orden, y
            nosotros trabajamos en algo que se siente cuidado, no en un software hospitalario
            genérico.»
          </blockquote>
          <div className="mt-10 flex items-center justify-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-ml-primary text-lg font-semibold text-ml-on-primary">
              ER
            </span>
            <div className="text-left">
              <p className="font-headline text-xl italic text-ml-primary">Dra. Elena Ríos</p>
              <p className="text-sm text-ml-on-surface-variant">Directora médica, Lumina Health</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────── CTA ─────────────────────────── */}
      <section className="mx-auto max-w-6xl px-6 pb-24">
        <div
          data-reveal
          className="relative overflow-hidden rounded-lg px-8 py-16 text-center sm:px-16 sm:py-20"
          style={sceneMesh}
        >
          <h2 className="mx-auto max-w-2xl font-headline text-3xl font-light leading-tight tracking-[-0.02em] text-white sm:text-5xl">
            Eleve el estándar de su clínica
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-white/70">
            Únase a los equipos clínicos que descubrieron lo que se siente trabajar con un
            sistema que de verdad los respalda.
          </p>
          <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row">
            <button
              onClick={goLogin}
              className="ml-cta group inline-flex items-center justify-center gap-2 rounded-lg bg-ml-surface-container-lowest px-8 py-4 font-semibold text-ml-primary hover:bg-white"
            >
              <Plus className="h-4 w-4" />
              Solicitar acceso
            </button>
            <button className="ml-cta inline-flex items-center justify-center gap-2 rounded-lg border border-white/25 px-8 py-4 font-semibold text-white hover:bg-white/10">
              Hablar con ventas
            </button>
          </div>
        </div>
      </section>

      {/* ─────────────────────────── FOOTER ─────────────────────────── */}
      <footer className="border-t border-ml-outline-variant/70 bg-ml-surface-container-low">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <div className="grid grid-cols-2 gap-10 md:grid-cols-4">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-ml-primary text-ml-on-primary">
                  <Heart className="h-5 w-5" />
                </span>
                <span className="flex flex-col leading-none">
                  <span className="font-headline text-xl italic text-ml-primary">MediLux</span>
                  <span className="mt-0.5 text-[0.6rem] uppercase tracking-[0.2em] text-ml-on-surface-variant">
                    Clinical Atelier
                  </span>
                </span>
              </div>
              <p className="mt-5 max-w-xs text-sm leading-relaxed text-ml-on-surface-variant">
                Software de gestión clínica para equipos que valoran el orden, la claridad y el
                tiempo con el paciente.
              </p>
            </div>
            {[
              { h: 'Plataforma', links: ['Expediente', 'Agenda', 'Facturación', 'Seguridad'] },
              { h: 'Compañía', links: ['Nosotros', 'Carreras', 'Contacto', 'Prensa'] },
              { h: 'Legal', links: ['Términos', 'Privacidad', 'Aviso NOM-024', 'Cumplimiento'] },
            ].map((col) => (
              <div key={col.h}>
                <h4 className="text-xs font-semibold uppercase tracking-[0.16em] text-ml-primary">
                  {col.h}
                </h4>
                <ul className="mt-5 space-y-3">
                  {col.links.map((l) => (
                    <li key={l}>
                      <a
                        href="#"
                        className="text-sm text-ml-on-surface-variant transition-colors duration-200 hover:text-ml-primary"
                      >
                        {l}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-ml-outline-variant/60 pt-8 sm:flex-row">
            <p className="text-xs text-ml-on-surface-variant">
              © 2026 MediLux Clinical Atelier. Todos los derechos reservados.
            </p>
            <div className="flex items-center gap-4 text-ml-on-surface-variant">
              <a href="#" aria-label="Twitter" className="transition-colors duration-200 hover:text-ml-primary">
                <Twitter className="h-5 w-5" />
              </a>
              <a href="#" aria-label="LinkedIn" className="transition-colors duration-200 hover:text-ml-primary">
                <Linkedin className="h-5 w-5" />
              </a>
              <a href="#" aria-label="Instagram" className="transition-colors duration-200 hover:text-ml-primary">
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
